import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface CustomerReferralReq {
  id?: number;
  merchantId?: number;
  customerId: number;
  vendorId?: number;
  referredName: string;
  phoneNo: string;
  email?: string;
  referralDate?: string;
  remark?: string;
  status?: string;
  addedBy?: string;
  addedByUserId?: number;
}

export interface CustomerReferralResp {
  id: number;
  merchantId?: number;
  customerId: number;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  vendorId?: number;
  vendorName?: string;
  referredName: string;
  phoneNo: string;
  email?: string;
  referralDate?: string;
  remark?: string;
  status: string;
  addedBy?: string;
  addedByUserId?: number;
  createdDate?: string;
}

export interface CustomerReferralSummaryResp {
  customerId: number;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  vendorId?: number;
  vendorName?: string;
  totalReferrals: number;
  convertedReferrals: number;
  pendingReferrals: number;
  lastReferralDate?: string;
}

const baseUrl = process.env.REACT_APP_TOURISM_CLASSIFIED_API_URL || '';

export const referralServices = createApi({
  reducerPath: 'referralService',
  tagTypes: ['ReferralTag'],
  baseQuery: fetchBaseQuery({
    baseUrl: baseUrl,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('jwtToken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  endpoints: (builder) => ({
    saveReferral: builder.mutation<CustomerReferralResp, CustomerReferralReq>({
      query: (body) => ({
        url: 'customer-referral/save',
        method: 'POST',
        body
      }),
      invalidatesTags: ['ReferralTag']
    }),
    updateReferral: builder.mutation<CustomerReferralResp, CustomerReferralReq>({
      query: (body) => ({
        url: 'customer-referral/update',
        method: 'PUT',
        body
      }),
      invalidatesTags: ['ReferralTag']
    }),
    getReferralsByCustomerId: builder.query<CustomerReferralResp[], number>({
      query: (customerId) => ({
        url: `customer-referral/by-customer/${customerId}`,
        method: 'GET'
      }),
      providesTags: ['ReferralTag']
    }),
    getReferralsByVendorId: builder.query<CustomerReferralResp[], number>({
      query: (vendorId) => ({
        url: `customer-referral/by-vendor/${vendorId}`,
        method: 'GET'
      }),
      providesTags: ['ReferralTag']
    }),
    getAllReferrals: builder.query<CustomerReferralResp[], void>({
      query: () => ({
        url: 'customer-referral/all',
        method: 'GET'
      }),
      providesTags: ['ReferralTag']
    }),
    getCustomerReferralSummaries: builder.query<CustomerReferralSummaryResp[], number | undefined>({
      query: (vendorId) => ({
        url: vendorId ? `customer-referral/customer-summary?vendorId=${vendorId}` : 'customer-referral/customer-summary',
        method: 'GET'
      }),
      providesTags: ['ReferralTag']
    }),
    deleteReferral: builder.mutation<boolean, number>({
      query: (id) => ({
        url: `customer-referral/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['ReferralTag']
    })
  })
});

export const {
  useSaveReferralMutation,
  useUpdateReferralMutation,
  useGetReferralsByCustomerIdQuery,
  useGetReferralsByVendorIdQuery,
  useGetAllReferralsQuery,
  useGetCustomerReferralSummariesQuery,
  useDeleteReferralMutation
} = referralServices;
